<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div
        class="app-content-wrapper rd-command-center"
        :class="{ 'app-content-wrapper--viewport-locked': isViewportLocked }"
        :style="{ '--rd-sidebar-offset': contentSidebarOffset, '--rd-content-viewport-height': contentViewportHeight }"
      >
        <VaIconMenuCollapsed
          v-if="!isMobile"
          class="rd-sidebar-edge-toggle cursor-pointer"
          :class="{ 'x-flip': !isSidebarMinimized }"
          :color="edgeToggleColor"
          @click="isSidebarMinimized = !isSidebarMinimized"
        />
        <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
          <div v-if="isFullScreenSidebar" class="flex justify-end">
            <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
          </div>
        </div>
        <main class="app-layout__main p-4 pt-0" :class="{ 'app-layout__main--viewport-locked': isViewportLocked }">
          <article>
            <RouterView />
          </article>
        </main>
      </div>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useBreakpoint, useColors } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import VaIconMenuCollapsed from '../components/icons/VaIconMenuCollapsed.vue'

const GlobalStore = useGlobalStore()
const route = useRoute()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const contentViewportHeight = ref('100%')
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const updateContentViewportHeight = () => {
  const topArea = document.querySelector('.va-layout__area--top')
  const topHeight = topArea?.getBoundingClientRect().height ?? 0
  contentViewportHeight.value = topHeight > 0 ? `calc(100vh - ${topHeight}px)` : '100vh'
}

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
  updateContentViewportHeight()
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
  requestAnimationFrame(updateContentViewportHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const contentSidebarOffset = computed(() => {
  return isTablet.value && !isMobile.value && isSidebarMinimized.value ? '56px' : '0px'
})

const isViewportLocked = computed(() => route.meta.viewportLocked === true)

const { getColor } = useColors()
const edgeToggleColor = computed(() => getColor('secondary'))

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
.app-content-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  padding-left: var(--rd-sidebar-offset);
  overflow-x: hidden;
  transition: padding-left 0.2s ease;
}

.rd-sidebar-edge-toggle {
  position: absolute;
  top: 14px;
  left: 0;
  z-index: 4;
  transform: translateX(-50%);
  transform-origin: center;
}

.rd-sidebar-edge-toggle.x-flip {
  transform: translateX(-50%) scaleX(-1);
}

.app-layout__main,
.app-layout__main article {
  min-width: 0;
  max-width: 100%;
}

.app-content-wrapper.rd-command-center.app-content-wrapper--viewport-locked {
  min-height: 0;
  height: var(--rd-content-viewport-height, 100%);
  overflow-y: hidden;
}

.app-layout__main--viewport-locked {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.app-layout__main--viewport-locked article {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

@media (max-width: 900px) {
  .app-content-wrapper.rd-command-center.app-content-wrapper--viewport-locked {
    min-height: 100vh;
    height: auto;
    overflow-y: auto;
  }

  .app-layout__main--viewport-locked,
  .app-layout__main--viewport-locked article {
    height: auto;
    overflow: visible;
  }
}
</style>
